import _ from "lodash";
import React, { useLayoutEffect } from "react";
import { getAssessmentToolNodeDetailsQuery } from "modules/CommonQuery";
import { getAssessmentToolNodeDetailsFromCache } from "modules/CommonGraphqlHelpers";
import {
  SKIP_ASSESSMENT_TOOL_NODE_TOOLS,
  getDPCriteriaRubricHeaderSubtext,
} from "ClassRoom/modules/utils";
import { graphql, compose } from "lib/apolloClient";
import I18nHOC from "UIComponents/I18nHOC";
import withLoader from "UIComponents/GqlWithLoader";
import classes from "./SelectEditTemplate.scss";
import { connect } from "react-redux";
import { hasModuleAdminAccess, MODULES } from "lib/staffPermissionUtils";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";

import { STANDARD_BASED_RUBRIC } from "Constants/stringConstants";
import { syncStandardsWithStandardBasedRubric } from "modules/AssessmentTool/AssessmentToolModule";
import { getCurrentCurriculumProgramType } from "modules/CommonSelectors";
import { STANDARD_UIDS } from "modules/Services/ServicesConstants";

const SelectTemplate = props => {
  const {
    t,
    renderTool,
    assessmentTool,
    updateCurrentStep,
    onCopyTool,
    updateFooterDetails,
    toggleAssessmentToolModal,
    updateHeaderDetails,
    type,
    subjectName,
    updateSelectedCriteria,
    isAdmin,
    templateCreatorId,
    userId,
    convertedScoreBasedRubricId,
    assessmentId,
    syncStandardsWithStandardBasedRubric,
    assessmentFields,
    curriculumProgramType,
  } = props;

  const handleEditTemplateClick = () => {
    updateCurrentStep("EDIT_TEMPLATE");
    updateFooterDetails({
      showButton1: false,
      showButton2: false,
      showButton3: true,
      button3Label: t("common:done"),
      button3Variant: "progressive",
      button3ClickAction: () => {
        updateCurrentStep("SELECT_TEMPLATE");
      },
    });
    updateHeaderDetails({
      showCloseButton: false,
      subtext: "",
    });
  };

  const handleUseTemplateClick = () => {
    // if convertedScoreBasedRubricId is present it means rubric is converted to score based rubric and is already added to the assessment
    // so no need to copy the tool again
    if (!convertedScoreBasedRubricId) {
      const toolId = _.get(assessmentTool, "id");
      onCopyTool(toolId);
    }

    if (type === STANDARD_BASED_RUBRIC) {
      const standardUID = STANDARD_UIDS[curriculumProgramType];
      const standardField = _.find(assessmentFields, { uid: standardUID });
      const initialSelectedStandards = _.get(standardField, "value", []);

      const standardRubricCriterias = _.get(
        assessmentTool,
        "standardRubricCriterias",
        []
      );

      const selectedStandards = _.compact(
        _.map(standardRubricCriterias, criteria => {
          const { standard, benchmark } = criteria || {};
          return standard?.id || benchmark?.id;
        })
      );

      syncStandardsWithStandardBasedRubric({
        assessmentId,
        initialSelectedStandards, // already selected standards in the learning goals section
        selectedStandards, // standards in the rubric,
        isTemplate: true,
      });
    }

    toggleAssessmentToolModal();
  };

  const title = assessmentTool?.title ?? assessmentTool?.label;

  const templateName = !_.isEmpty(title) ? title : t("common:untitled");

  const subtext = getDPCriteriaRubricHeaderSubtext({ assessmentTool, t });

  useLayoutEffect(() => {
    const isDPRubric = type === "DP_ASSESSMENT_RUBRIC";

    updateHeaderDetails({
      title: isDPRubric
        ? t("common:dp_assessment_criteria_for_label", { label: subjectName })
        : templateName,
      subtext: isDPRubric ? subtext : "",
      showBackButton: true,
      showCloseButton: true,
      backButtonClickAction: () => updateCurrentStep("TEMPLATE_LIST"),
    });

    const isTemplateCreator = _.isEqual(userId, templateCreatorId);

    updateFooterDetails({
      showFooter: true,
      showButton1:
        type !== "DP_ASSESSMENT_RUBRIC" && (isAdmin || isTemplateCreator),
      showButton2: false,
      showButton3: true,
      button1Variant: "progressive",
      legacyButton1Variant: "inline-progressive",
      button1Type: "inline",
      button1Label: t("common:edit_original_template"),
      button1ClickAction: handleEditTemplateClick,
      button3Label: isDPRubric
        ? t("common:select_with_label", {
            label: t("common:assessment_criteria"),
          })
        : t("classRoom:add_to_task"),
      button3Variant: "progressive",
      button3ClickAction: handleUseTemplateClick,
    });

    if (isDPRubric) {
      // by default, select all criteria
      updateSelectedCriteria?.(_.map(_.get(assessmentTool, "criteria"), "id"));
    }
  }, []);

  return (
    <div className={classes.container}>
      {renderTool({ mode: "view", assessmentTool })}
    </div>
  );
};

const EditTemplate = props => {
  const {
    t,
    renderTool,
    assessmentTool,
    updateCurrentStep,
    updateHeaderDetails,
  } = props;

  const title = _.get(assessmentTool, "title", "");
  const templateName = !_.isEmpty(title) ? title : t("common:untitled");

  useLayoutEffect(() => {
    updateHeaderDetails({
      title: templateName,
      subtext: "",
      showBackButton: true,
      backButtonClickAction: () => updateCurrentStep("SELECT_TEMPLATE"),
    });
  }, []);

  return (
    <div className={classes.container}>{renderTool({ assessmentTool })}</div>
  );
};

const SelectEditTemplate = props => {
  const { currentStep } = props;

  switch (currentStep) {
    case "SELECT_TEMPLATE": {
      return <SelectTemplate {...props} />;
    }
    case "EDIT_TEMPLATE": {
      return <EditTemplate {...props} />;
    }
  }
};

const mapStateToProps = state => {
  const userInfo = getUserInfo();
  const userId = userInfo.id;
  const isAdmin = hasModuleAdminAccess({
    moduleName: MODULES.UNIT_PLANNING.id,
  });

  const curriculumProgramType = getCurrentCurriculumProgramType(state);

  return {
    isAdmin,
    userId,
    curriculumProgramType,
  };
};

const mapActionCreators = { syncStandardsWithStandardBasedRubric };

export default compose(
  I18nHOC([{ resource: ["common", "academicSetup"] }]),
  connect(mapStateToProps, mapActionCreators),
  graphql(getAssessmentToolNodeDetailsQuery, {
    name: "getAssessmentToolNodeDetails",
    skip: ({ entityType }) =>
      _.includes(SKIP_ASSESSMENT_TOOL_NODE_TOOLS, entityType),
    options: ({ toolId, entityType, portalType }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: toolId, type: entityType, portalType },
    }),
    props({
      getAssessmentToolNodeDetails,
      ownProps: { isData, isLoading, toolId, entityType },
    }) {
      const queryData = getAssessmentToolNodeDetailsFromCache({
        id: toolId,
        type: entityType,
      });
      const networkStatus = _.get(
        getAssessmentToolNodeDetails,
        "networkStatus"
      );

      const templateCreatorId = _.get(queryData, "createdBy.id");

      return {
        assessmentTool: queryData,
        templateCreatorId,
        isData: !_.isEmpty(queryData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),
  withLoader
)(SelectEditTemplate);
